import { forwardRef, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
  IconAlignLeft,
  IconArrowLeft,
  IconDeviceFloppy,
  IconRefresh,
} from '@rossum/ui/icons/tabler';
import {
  CircularProgress,
  IconButton,
  IconButtonProps,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import styles from '../styles.module.sass';
import DocumentationLinks from './DocumentationLinks';
import { TemporaryMessageT } from './hooks';
import InfoMessage, { EditorType } from './InfoMessage';

type Props = {
  title?: string | ReactNode;
  right: ReactNode;
  onPrettify: () => void;
  canPrettify: boolean;
  onSave: () => void;
  canSave: boolean;
  onReset: () => void;
  canReset: boolean;
  isValid: boolean;
  isChanged: boolean;
  onNavigateBack?: () => void;
  canNavigateBack?: boolean;
  editorType: EditorType;
  isPending?: boolean;
  temporaryMessage?: TemporaryMessageT;
  savingFailed?: boolean;
  isSaving?: boolean;
};

export const TopMenuButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (props, ref) => <IconButton ref={ref} {...props} color="inherit" />
);

const TopMenu = ({
  canNavigateBack,
  canPrettify,
  canReset,
  canSave,
  isChanged,
  isValid,
  onNavigateBack,
  onPrettify,
  onReset,
  onSave,
  right,
  title,
  editorType,
  isPending,
  temporaryMessage,
  savingFailed,
  isSaving,
}: Props) => {
  const intl = useIntl();
  return (
    <Stack
      data-cy="editor-top-menu"
      justifyContent="space-between"
      direction="row"
      sx={{
        px: 2,
        height: 54,
        backgroundColor: theme => theme.palette.background.default,
        alignItems: 'center',
        zIndex: 1,
        flex: '0 0 auto',
      }}
    >
      <Stack
        direction="row"
        spacing={2.5}
        alignItems="center"
        className={styles['EditorControls']}
      >
        {onNavigateBack && (
          <Tooltip
            title={intl.formatMessage({
              id: 'components.editor.tooltips.navigateBack',
            })}
          >
            <span>
              <TopMenuButton
                onClick={onNavigateBack}
                disabled={!canNavigateBack}
                data-cy="exit-route-button"
              >
                <SvgIcon fontSize="large">
                  <IconArrowLeft />
                </SvgIcon>
              </TopMenuButton>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={
            canPrettify
              ? intl.formatMessage({
                  id: 'components.editor.tooltips.prettify',
                })
              : ''
          }
        >
          <span>
            <TopMenuButton
              onClick={onPrettify}
              disabled={!canPrettify}
              data-cy="prettify-button"
            >
              <SvgIcon fontSize="large">
                <IconAlignLeft />
              </SvgIcon>
            </TopMenuButton>
          </span>
        </Tooltip>
        <Tooltip
          title={intl.formatMessage({
            id: isPending
              ? 'components.editor.tooltips.save.pending'
              : 'components.editor.tooltips.save',
          })}
        >
          <span>
            <TopMenuButton
              onClick={onSave}
              disabled={!canSave}
              data-cy="save-button"
            >
              {isSaving ? (
                <CircularProgress size={20} color="inherit" sx={{ mx: 0.25 }} />
              ) : (
                <SvgIcon fontSize="large">
                  <IconDeviceFloppy />
                </SvgIcon>
              )}
            </TopMenuButton>
          </span>
        </Tooltip>
        <Tooltip
          title={intl.formatMessage({
            id: isPending
              ? 'components.editor.tooltips.reset.pending'
              : 'components.editor.tooltips.reset',
          })}
        >
          <span>
            <TopMenuButton
              onClick={onReset}
              disabled={!canReset}
              data-cy="reset-button"
            >
              <SvgIcon fontSize="large">
                <IconRefresh />
              </SvgIcon>
            </TopMenuButton>
          </span>
        </Tooltip>
        <DocumentationLinks editorType={editorType} />
        <div>
          <InfoMessage
            isPending={isPending ?? false}
            editorType={editorType}
            isValid={isValid}
            isChanged={isChanged}
            temporaryMessage={temporaryMessage}
            savingFailed={savingFailed ?? false}
          />
        </div>
      </Stack>
      {title && <div className={styles['Title']}>{title}</div>}
      <div className={styles['OnRight']}>{right}</div>
    </Stack>
  );
};

export default TopMenu;
